import { ExpressionSpecification } from 'mapbox-gl'
import { RGBA, colorBetween } from './rgba'
import md5 from 'md5'
import { LocaleString } from '@/api-client/models/utilization/Contracts'

export type ColorScaleType = 'STEP' | 'INTERPOLATE' | 'SINGLE'

export type Cutoff = {
  value: number
  opacity?: number
  label?: LocaleString
  color: string
}

export type ColorScaleInterpolate = {
  type: 'INTERPOLATE'
  orientation?: 'vertical' | 'horizontal'
  cutoffs: Cutoff[]
}

export type ColorScaleStep = {
  type: 'STEP'
  orientation?: 'vertical' | 'horizontal'
  cutoffs: [Omit<Cutoff, 'value'>, ...Cutoff[]]
}

export type ColorScaleSingle = {
  type: 'SINGLE'
  orientation?: 'vertical' | 'horizontal'
  cutoffs: [Omit<Cutoff, 'value'>]
}

export type ColorScaleConfig = ColorScaleSingle | ColorScaleStep | ColorScaleInterpolate

export type SVGGradient = {
  type: 'linearGradient' | 'radialGradient'
  id: string
  p1: [number, number]
  p2: [number, number]
  stops: {
    offset: number
    opacity?: number
    color: string
  }[]
}

const toRGBA = (color: string) => RGBA.fromHex(color)

export function getColorForValue(config: ColorScaleConfig, value: number): RGBA {
  switch (config.type) {
    case 'INTERPOLATE':
      return interpolateColor(config, value)
    case 'STEP':
      return stepColor(config, value)
    case 'SINGLE':
      return toRGBA(config.cutoffs[0].color)
  }
}

function interpolateColor(config: ColorScaleInterpolate, value: number): RGBA {
  if (value <= config.cutoffs[0].value) {
    return toRGBA(config.cutoffs[0].color)
  }
  for (let i = 1; i < config.cutoffs.length; i++) {
    const upper = config.cutoffs[i]
    const lower = config.cutoffs[i - 1]
    if (value >= lower.value && value <= upper.value) {
      const ratio = (value - lower.value) / (upper.value - lower.value)
      return colorBetween(toRGBA(lower.color), toRGBA(upper.color), ratio)
    }
  }
  return toRGBA(config.cutoffs[config.cutoffs.length - 1].color)
}

export function toCssLinearGradient(config: ColorScaleInterpolate): string {
  const cutoffs = config.cutoffs.slice(1)
  cutoffs.reverse()
  const stops = cutoffs.map((cutoff) => `${cutoff.color} ${cutoff.value}%`).join(', ')
  return `linear-gradient(to ${config.orientation ?? 'right'}, ${
    config.cutoffs[0].color
  } 0%, ${stops})`
}

function stepColor(config: ColorScaleStep, value: number): RGBA {
  const cutoffs = config.cutoffs.slice(1) as Cutoff[]
  cutoffs.reverse()
  for (const element of cutoffs) {
    const cutoff = element
    if (value >= cutoff.value) {
      return toRGBA(cutoff.color)
    }
  }
  return toRGBA(config.cutoffs[0].color)
}

type InterpolationConfig = ['interpolate', ['linear'], ['get', string], ...(number | string)[]]
type StepConfig = ['step', ['get', string], ...(number | string)[]]

export function configToMapboxExpression(
  fieldName: string,
  config: ColorScaleConfig,
  fallback: ExpressionSpecification | RGBA = RGBA.transparent()
): ExpressionSpecification {
  return [
    'case',
    ['!=', ['feature-state', 'color'], null],
    ['feature-state', 'color'],
    ['!=', ['get', fieldName], null],
    configToMapboxColorExpression(fieldName, config),
    fallback instanceof RGBA ? fallback.toString() : fallback
  ]
}

function configToMapboxColorExpression(
  fieldName: string,
  config: ColorScaleConfig
): InterpolationConfig | StepConfig | string {
  switch (config.type) {
    case 'INTERPOLATE':
      return [
        'interpolate',
        ['linear'],
        ['get', fieldName],
        ...config.cutoffs.flatMap((cutoff) => [cutoff.value ?? 0, toRGBA(cutoff.color).toString()])
      ]
    case 'STEP': {
      const step = [
        'step',
        ['get', fieldName],
        toRGBA(config.cutoffs[0].color).toString(),
        ...(config.cutoffs.slice(1) as Cutoff[]).flatMap((cutoff) => [
          cutoff.value ?? 0,
          toRGBA(cutoff.color).toString()
        ])
      ] satisfies StepConfig
      return config.cutoffs.length > 1 ? step : toRGBA(config.cutoffs[0].color).toString()
    }
    case 'SINGLE':
      return toRGBA(config.cutoffs[0].color).toString()
  }
}

/**
 * This function probably have some bugs, that is why there are many ts-ignore.
 * It should be refactored to not assume that ColorScaleStep has a value in the first cutoff.
 */
export function adjustCutoffWithLimits(
  config: ColorScaleInterpolate | ColorScaleStep | undefined,
  limits: [number, number]
): ColorScaleInterpolate | ColorScaleStep | undefined {
  if (!config) return undefined

  const stops = config.cutoffs.slice()
  // Sort the stops in ascending order based on the offset
  // @ts-expect-error - ignore that value can be missing
  stops.sort((a, b) => a.value - b.value)

  // Filter the stops to only include those within the yLimits range
  // @ts-expect-error - ignore that value can be missing
  const filteredStops = stops.filter((stop) => stop.value >= limits[0] && stop.value <= limits[1])

  // Check if the first stop matches the lower yLimit, if not, add a new stop at the lower limit
  // @ts-expect-error - ignore that value can be missing
  if (filteredStops[0]?.value !== limits[0]) {
    filteredStops.unshift({
      value: limits[0],
      color: getColorForValue(config, limits[0]).toHex()
    })
  }

  // Check if the last stop matches the upper yLimit, if not, add a new stop at the upper limit
  // @ts-expect-error - ignore that value can be missing
  if (filteredStops[filteredStops.length - 1]?.value !== limits[1]) {
    filteredStops.push({ value: limits[1], color: getColorForValue(config, limits[1]).toHex() })
  }

  return {
    ...config,
    // @ts-expect-error - the typesystem is not smart enough to know that the types are matching
    cutoffs: filteredStops
  }
}

export function configToSvgGradient(
  config: ColorScaleInterpolate,
  values: number[]
): SVGGradient | undefined {
  const cutoffs = config.cutoffs.slice()
  cutoffs.sort((a, b) => a.value - b.value)

  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)

  const cutoffsBetween = cutoffs
    .filter((cutoff) => cutoff.value > minValue && cutoff.value < maxValue)
    .map((cutoff) => ({
      offset: (cutoff.value - minValue) / (maxValue - minValue),
      opacity: cutoff.opacity,
      color: cutoff.color
    }))

  const gradient: SVGGradient = {
    type: 'linearGradient',
    id: `gradient-${md5(JSON.stringify(config) + minValue.toString() + maxValue.toString())}`,
    p1: config.orientation === 'horizontal' ? [0, 0] : [0, 1],
    p2: config.orientation === 'horizontal' ? [1, 0] : [0, 0],
    stops: [
      {
        offset: 0,
        color: getColorForValue(config, minValue).toHex(),
        opacity: cutoffsBetween[0]?.opacity ?? cutoffs[0]?.opacity
      },
      ...cutoffsBetween,
      {
        offset: 1,
        color: getColorForValue(config, maxValue).toHex(),
        opacity:
          cutoffsBetween[cutoffsBetween.length - 1]?.opacity ?? cutoffs[cutoffs.length - 1]?.opacity
      }
    ]
  }

  return gradient
}

export function controlledColorScale<T extends ColorScaleType>(
  type: T,
  edges: number[],
  colorBeforeEdge: string[]
): ColorScaleConfig | undefined {
  const DEFAULT =
    type === 'STEP'
      ? ({
          type,
          cutoffs: [{ color: colorBeforeEdge[Math.floor(colorBeforeEdge.length / 2)] }]
        } as ColorScaleStep)
      : ({
          type,
          cutoffs: [
            { value: 0, color: colorBeforeEdge[0] },
            { value: 1, color: colorBeforeEdge[colorBeforeEdge.length - 1] }
          ]
        } as ColorScaleInterpolate)
  if (
    edges.length === 0 ||
    (edges.length !== 1 && edges.length !== colorBeforeEdge.length + 1) ||
    (edges.length === 1 && colorBeforeEdge.length !== 2)
  ) {
    return undefined
  }

  const cutoffs = colorBeforeEdge
    .map(
      (color, index) =>
        [
          edges[Math.min(index, edges.length - 1)],
          edges[Math.min(index + 1, edges.length - 1)],
          color
        ] as [number, number, string]
    )
    .filter((pair) => pair[0] !== pair[1] || edges.length === 1)
    .flatMap(([start, end, color]) => [
      { value: start, color },
      { value: end, color }
    ])
    .toReversed()
    .reduce((acc, cutoff) => {
      if ('value' in cutoff && !acc.some((c) => c.value === cutoff.value)) {
        acc.push(cutoff)
      }
      return acc
    }, [] as Cutoff[])
    .sort((a, b) => a.value - b.value)
  if (edges.length === 1) {
    cutoffs.unshift({ color: colorBeforeEdge[0] } as Cutoff)
  }
  if (cutoffs.length === 0) {
    return DEFAULT
  }
  return type === 'STEP'
    ? ({
        type,
        cutoffs
      } as ColorScaleStep)
    : ({
        type,
        cutoffs
      } as ColorScaleInterpolate)
}

export const createColorScale = (
  color: string,
  from: number,
  to: number
): ColorScaleInterpolate | undefined =>
  from === to
    ? undefined
    : {
        type: 'INTERPOLATE',
        cutoffs: [
          {
            value: from,
            color: colorBetween(RGBA.fromHex('#ffffff'), RGBA.fromHex(color), 0.2).toHex()
          },
          { value: (from + to) / 2, color: RGBA.fromHex(color).toHex() },
          {
            value: to,
            color: colorBetween(RGBA.fromHex('#000000'), RGBA.fromHex(color), 0.75).toHex()
          }
        ]
      }
